"use client";

import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import { Drawer } from "vaul";
import {
  WalletInfoContext,
  type WalletInfoView,
} from "../providers/wallet-info-provider";
import type { DrawerDirection, Environment, Theme } from "../../../types";
import { type ConnectorId, CONNECTOR_REGISTRY, type PreferredToken, type TokenBalance, type WalletInfoStyle } from "../types";
import type {
  EstimateGasFn,
  GetTransactionReceiptFn,
  LpBalanceReaderFn,
  PortfolioSection,
  SendTransactionFn,
  StakingRewardsReaderFn,
} from "../../wallet-portfolio/types";
import {
  clearRecentSendAddresses,
  type SendAccount,
  type SendAsset,
} from "../../send";
import type {
  BridgeApproveFn,
  BridgeHistoryItem,
  BridgeGetApprovalFn,
  BridgeGetToTokensFn,
  BridgeQuoteFn,
  BridgeSubmitFn,
  BridgeToken,
} from "../../bridge";
import type { OnePopSummary } from "../../onepop";
import type { OnOutlink } from "../../../lib/outlink";
import { DEFAULT_TERMS_URL, DEFAULT_PRIVACY_URL } from "../../../lib/constants";
import { resolveEnvironment } from "../../../lib/environment";
import { useHistoryDismiss } from "../../../hooks/use-history-dismiss";
import { useMediaQuery } from "../../../hooks/use-media-query";
import { setDappUiAnalyticsUser } from "../../../lib/sentry/index";
import { WalletInfoTrigger } from "./WalletInfoTrigger";
import { WalletInfoContent } from "./WalletInfoContent";
import { WalletInfoNav } from "./WalletInfoNav";
import { WalletInfoFooter } from "./WalletInfoFooter";
import "../styles/theme.css";
import "../styles/base.css";
import "../styles/keyframes.css";
import "../styles/component.css";
import "../styles/token-detail.css";
import "../styles/utilities.css";

export interface WalletInfoProps {
  env?: Environment;
  theme?: Theme;
  mobileBreakpoint?: number;
  drawerDirection?: DrawerDirection;
  modal?: boolean;
  showBalance?: boolean;
  showForgeToken?: boolean;
  showGameToken?: boolean;
  showQR?: boolean;
  /**
   * Bridge 액션 노출 여부 (기본 true).
   * - true: 액션 행에 Bridge 버튼, Receive는 상단 QR 버튼으로 표기
   * - false: 액션 행에 Receive 버튼, 상단 QR 버튼 미표기
   */
  showBridge?: boolean;
  qrLogoSrc?: string;
  walletAddress: string;
  accountName?: string;
  sendAccounts?: SendAccount[];
  /** 헤더 좌측에 표시될 프로필 이미지 URL. 미지정 시 주소 기반 그라디언트 아바타가 자동 생성됨. */
  profileImageUrl?: string;
  /** Resolves connectorName & connectorIconUrl from CONNECTOR_REGISTRY */
  connectorId?: ConnectorId;
  connectorName?: string;
  connectorIconUrl?: string;
  preferredTokens?: PreferredToken[];
  onSelectWallet?: () => void;
  onCopyAddress?: (address: string, success: boolean) => void;
  /**
   * WalletInfo 기본 액션 row의 Buy 클릭 핸들러. Promise를 반환하면 reject 시
   * Buy 카드가 `onBuyDisabledMessage`와 동일한 토스트로 실패를 안내한다.
   */
  onBuy?: () => void | Promise<void>;
  /**
   * Buy 카드를 시각적으로 비활성 상태로 표시하고, 사용자가 클릭하면 이
   * 메시지를 토스트로 띄운다. `onBuy`보다 우선. 예: on-ramp가 사용자 국가에서
   * 차단된 경우 안내 메시지 전달용.
   */
  onBuyDisabledMessage?: string;
  /**
   * Bridge 카드를 시각적으로 비활성 상태로 표시하고, 클릭하면 이 메시지를
   * 토스트로 띄운다. 예: 연결된 지갑이 CROSSx 계열이 아니라 브릿지를
   * 지원하지 않는 경우.
   */
  onBridgeDisabledMessage?: string;
  /**
   * Send 카드(및 토큰 행 탭으로 진입하는 전송 플로우)를 시각적으로 비활성
   * 상태로 표시하고, 클릭하면 이 메시지를 토스트로 띄운다. 예: 연결된 지갑이
   * CROSSx 계열이 아닌 경우.
   */
  onSendDisabledMessage?: string;
  /**
   * 지정 시 기본 Disconnect 버튼이 Footer에 노출됨. `WalletInfo.Footer` 슬롯이
   * 있으면 해당 슬롯이 메인 영역을 대체한다. Terms/Privacy 링크는 둘 중 무엇을
   * 쓰든 항상 하단에 함께 렌더된다.
   */
  onDisconnect?: () => void;
  /** 기본 Disconnect 버튼 라벨 (기본 "Disconnect"). */
  disconnectLabel?: string;
  /**
   * Footer 하단 Terms 링크 URL.
   * 미지정 시 CROSS 에코시스템 기본 URL로 폴백되어 자동 노출된다.
   * 숨기려면 빈 문자열(`""`)을 전달.
   */
  termsUrl?: string;
  /** Terms 링크 라벨 (기본 "Terms of Service"). */
  termsLabel?: string;
  /**
   * Footer 하단 Privacy 링크 URL.
   * 미지정 시 CROSS 에코시스템 기본 URL로 폴백되어 자동 노출된다.
   * 숨기려면 빈 문자열(`""`)을 전달.
   */
  privacyUrl?: string;
  /** Privacy 링크 라벨 (기본 "Privacy Policy"). */
  privacyLabel?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Portfolio 뷰 사용 여부.
   * true면 기본 액션 row에 Portfolio 버튼이 표시되며, 클릭 시 내부 Portfolio 뷰로 전환됩니다.
   */
  showPortfolio?: boolean;
  /**
   * ONEpop 사용 여부 (기본 **false** — 서비스 오픈 전 딥링크가 죽은 링크가
   * 되지 않도록 opt-in). true면 기본 액션 row에 ONEpop 버튼이 표시되며,
   * 클릭 시 내부 ONEpop 뷰로 전환된다. 뷰는 순수 UI라 아래 데이터/콜백이
   * 없어도 렌더된다 — 액션 버튼은 전부 ONEpop 서비스 딥링크가 기본 동작이다.
   * connect-kit-react 경유라면 config `onePopEnabled: true` 하나로 노출과
   * 조회가 함께 켜진다.
   */
  showOnePop?: boolean;
  /**
   * ONEpop 뷰가 표시할 상태(수령 대기 수/합계, replies 카운트, 최근 활동).
   *
   * dapp-ui는 `@nexus-cross/pop`(viem 필수)에 의존할 수 없으므로 one-pop-api를
   * 직접 조회하지 않는다 — `/drops`·`/histories`는 SIWE JWT + X 연결이 필요해
   * 서명 가능한 호스트가 조회한 뒤 이 prop 으로 주입한다.
   * `@nexus-cross/connect-kit-react`를 쓰면 자동 주입된다.
   * 미주입 시 "내역 없음" 온보딩 화면을 그린다.
   */
  onePopSummary?: OnePopSummary;
  /**
   * ONEpop 뷰 진입 시 1회 호출. `onePopSummary` 조회는 SIWE 서명(지갑 팝업)을
   * 요구하므로, 주입측이 사용자가 실제로 ONEpop을 열 때까지 조회를 미루는
   * 트리거로 쓴다.
   */
  onOnePopOpen?: () => void;
  /** Send POP 카드 + "Send your first POP!" CTA. 미주입 시 비활성. */
  onOnePopSend?: () => void;
  /** Claim POPs 카드 + 수령 대기 배너. 미주입 시 비활성. */
  onOnePopClaim?: () => void;
  /** Activity 카드 + "See all activity" + replies 배너. 미주입 시 비활성. */
  onOnePopActivity?: () => void;
  /**
   * 활동 화면으로 나가는 경로에서 추가로 호출되는 알림 훅(목적지 이동은 그대로).
   * `onOnePopActivity` 와 달리 기본 동작을 대체하지 않는다 — 답장을 확인
   * 처리해 `onePopSummary.repliesWaitingCount` 를 떨어뜨리는 용도다.
   * connect-kit 경유라면 `useOnePopSummary().markRepliesSeen` 이 자동 배선된다.
   */
  onOnePopRepliesSeen?: () => void;
  /**
   * ONEpop Available 카드의 "Get ONEUSD ›" 자리를 대체할 노드.
   *
   * 지갑을 떠나지 않고 Get ONEUSD 위젯(Swap / Bridge / Transfer 선택 모달)을
   * 열기 위한 슬롯이다 — 위젯은 wagmi 어댑터가 필요하므로 dapp-ui 가 직접
   * 마운트하지 못한다. `@nexus-cross/connect-kit-react` 를 쓰면
   * `<CrossGetOneUsd mode="button" buttonComponent={OnePopGetTokenTrigger}>` 가
   * 자동 주입되고, 직접 쓰는 호스트는 같은 형태로 넘기면 된다.
   * 미주입 시 기존 게임토큰 브리지 딥링크가 그대로 동작한다.
   */
  onePopGetTokenSlot?: React.ReactNode;
  /** ONEpop 워드마크 이미지 URL. 미지정 시 내장 SVG 재현본. */
  onePopLogoSrc?: string;
  /** ONEpop 히어로 일러스트 URL. 미지정 시 내장 SVG 재현본. */
  onePopHeroSrc?: string;
  /** Portfolio 뷰의 헤더 타이틀 (기본 "My Portfolio"). */
  portfolioTitle?: string;
  /**
   * Portfolio 뷰에 노출할 섹션 필터. 미지정 시 모든 섹션을 기본 순서대로
   * 표시하고, 배열을 주면 포함된 섹션만 표시한다(빈 배열 = 전부 숨김).
   * 예: `["rewards", "staking"]`.
   */
  portfolioSections?: PortfolioSection[];
  /** Total Assets 섹션 노출 여부 (기본 true). showBalance=true일 때만 실제 표기. */
  showTotalAssets?: boolean;
  /** Total Assets 라벨 텍스트 (기본 "Total Assets USD"). */
  totalAssetsLabel?: string;
  /**
   * Send 페이지의 일반 토큰 전송에 사용할 외부 트랜잭션 전송 함수.
   * wagmi의 `sendTransactionAsync`를 그대로 전달해도 호환된다.
   */
  sendTransaction?: SendTransactionFn;
  getTransactionReceipt?: GetTransactionReceiptFn;
  /**
   * Send 확인 단계에서 표시할 가스/수수료 추정 함수.
   * 주입되지 않으면 확인 화면의 Gas/Est.Time/Max.Gas 행은 "—"로 표시된다.
   */
  estimateGas?: EstimateGasFn;
  /**
   * Terms/Privacy · 포트폴리오 섹션 등 외부 링크 이동을 가로채는 콜백.
   * `(url, ctx) => newUrl | null | undefined | Promise<...>` 형태이며
   *   - `string`      → 해당 URL로 이동(변형 가능)
   *   - `null`        → 이동 취소
   *   - `undefined`   → 원본 URL 유지
   * middle-click · cmd-click은 가로채지 않고 원본 `href`로 열린다.
   * `showPortfolio=true`일 때 내부 `WalletPortfolioBody`로도 릴레이된다.
   */
  onOutlink?: OnOutlink;
  /**
   * game-swap LP 섹션의 사용자별 LP 잔고/지분율을 온체인에서 보강하기 위한
   * 주입형 reader. `showPortfolio=true`일 때 `WalletPortfolioBody`로 릴레이된다.
   * 미주입 시 LP 섹션은 풀 정보만 표시한다.
   */
  lpBalanceReader?: LpBalanceReaderFn;
  /**
   * CROSS Staking 섹션의 온체인 `earned()` / `balanceOf()`를 보강하기 위한
   * 주입형 reader. `showPortfolio=true`일 때 `WalletPortfolioBody`로 릴레이된다.
   * 미주입 시 Staking 섹션은 API `cumulative_earned`로 폴백한다.
   */
  stakingRewardsReader?: StakingRewardsReaderFn;
  bridgeTokens?: BridgeToken[];
  bridgeHistory?: BridgeHistoryItem[];
  getBridgeQuote?: BridgeQuoteFn;
  getBridgeToTokens?: BridgeGetToTokensFn;
  getBridgeApproval?: BridgeGetApprovalFn;
  approveBridge?: BridgeApproveFn;
  submitBridge?: BridgeSubmitFn;
  /**
   * 상단 QR 버튼 / 기본 액션 row의 Receive / Send 콜백. (Buy는 위 onBuy
   * prop으로 이미 정의됨.)
   */
  onReceive?: () => void;
  /**
   * @deprecated Bridge 버튼은 항상 apps.json(gametokenBridge) 웹으로
   * 이동한다. 이 콜백은 더 이상 호출되지 않는다.
   */
  onBridge?: () => void;
  onSend?: () => void;
  style?: WalletInfoStyle;
  children: React.ReactNode;
}

function WalletInfoRoot({
  env,
  theme = "dark",
  mobileBreakpoint = 768,
  drawerDirection,
  modal,
  showBalance = false,
  showForgeToken = false,
  showGameToken = true,
  showQR = true,
  showBridge = true,
  qrLogoSrc,
  walletAddress,
  accountName,
  sendAccounts,
  profileImageUrl,
  connectorId,
  connectorName: connectorNameProp,
  connectorIconUrl: connectorIconUrlProp,
  preferredTokens = [],
  onSelectWallet,
  onCopyAddress,
  onBuy,
  onBuyDisabledMessage,
  onBridgeDisabledMessage,
  onSendDisabledMessage,
  onDisconnect,
  disconnectLabel = "Disconnect",
  termsUrl = DEFAULT_TERMS_URL,
  termsLabel = "Terms of Service",
  privacyUrl = DEFAULT_PRIVACY_URL,
  privacyLabel = "Privacy Policy",
  open: propOpen,
  onOpenChange,
  showPortfolio = false,
  showOnePop = false,
  onePopSummary,
  onOnePopOpen,
  onOnePopSend,
  onOnePopClaim,
  onOnePopActivity,
  onOnePopRepliesSeen,
  onePopGetTokenSlot,
  onePopLogoSrc,
  onePopHeroSrc,
  portfolioTitle = "My Portfolio",
  portfolioSections,
  showTotalAssets = true,
  totalAssetsLabel = "Total Assets USD",
  sendTransaction,
  getTransactionReceipt,
  estimateGas,
  onOutlink,
  lpBalanceReader,
  stakingRewardsReader,
  bridgeTokens,
  bridgeHistory,
  getBridgeQuote,
  getBridgeToTokens,
  getBridgeApproval,
  approveBridge,
  submitBridge,
  onReceive,
  onBridge,
  onSend,
  style,
  children,
}: WalletInfoProps) {
  const resolvedEnv = resolveEnvironment(env);
  const registryMeta = connectorId ? CONNECTOR_REGISTRY[connectorId] : undefined;
  const connectorName = connectorNameProp ?? registryMeta?.name;
  const connectorIconUrl = connectorIconUrlProp ?? registryMeta?.iconUrl;

  const isMobile = useMediaQuery(`(max-width: ${mobileBreakpoint}px)`);
  const resolvedDirection =
    drawerDirection ?? (showBalance ? "right" : "bottom");
  const isSideDrawer =
    resolvedDirection === "left" || resolvedDirection === "right";
  const resolvedModal = modal ?? false;
  const isControlled = propOpen !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = isControlled ? propOpen : internalOpen;

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const close = React.useCallback(
    () => handleOpenChange(false),
    [handleOpenChange],
  );

  // Android 백버튼 / iOS 백스와이프로 닫히게 한다.
  useHistoryDismiss(open, close);

  const [navContent, setNavContent] = React.useState<React.ReactNode | null>(
    null,
  );
  const [navPosition, setNavPosition] = React.useState<"top" | "bottom">("top");
  const [footerContent, setFooterContent] =
    React.useState<React.ReactNode | null>(null);
  const [view, setView] = React.useState<WalletInfoView>("wallet");
  const [selectedSendToken, setSelectedSendToken] =
    React.useState<SendAsset | null>(null);
  const [selectedDetailToken, setSelectedDetailToken] =
    React.useState<TokenBalance | null>(null);

  React.useEffect(() => {
    if (!open) {
      setView("wallet");
      setSelectedSendToken(null);
      setSelectedDetailToken(null);
    }
  }, [open]);

  React.useEffect(() => {
    setDappUiAnalyticsUser(walletAddress);
  }, [walletAddress]);

  const handleDisconnect = React.useCallback(() => {
    clearRecentSendAddresses(walletAddress);
    onDisconnect?.();
  }, [onDisconnect, walletAddress]);

  const contextValue = React.useMemo(
    () => ({
      env: resolvedEnv,
      theme,
      isMobile,
      open,
      drawerDirection: resolvedDirection,
      modal: resolvedModal,
      showBalance,
      showForgeToken,
      showGameToken,
      showQR,
      showBridge,
      qrLogoSrc,
      walletAddress,
      accountName,
      sendAccounts,
      profileImageUrl,
      connectorName,
      connectorIconUrl,
      preferredTokens,
      close,
      onSelectWallet,
      onCopyAddress,
      onBuy,
      onBuyDisabledMessage,
      onBridgeDisabledMessage,
      onSendDisabledMessage,
      onDisconnect: onDisconnect ? handleDisconnect : undefined,
      disconnectLabel,
      termsUrl,
      termsLabel,
      privacyUrl,
      privacyLabel,
      portalStyle: style,
      navContent,
      navPosition,
      setNavContent,
      setNavPosition,
      footerContent,
      setFooterContent,
      view,
      setView,
      selectedSendToken,
      setSelectedSendToken,
      selectedDetailToken,
      setSelectedDetailToken,
      showPortfolio,
      showOnePop,
      onePopSummary,
      onOnePopOpen,
      onOnePopSend,
      onOnePopClaim,
      onOnePopActivity,
      onOnePopRepliesSeen,
      onePopGetTokenSlot,
      onePopLogoSrc,
      onePopHeroSrc,
      portfolioTitle,
      portfolioSections,
      showTotalAssets,
      totalAssetsLabel,
      sendTransaction,
      getTransactionReceipt,
      estimateGas,
      onOutlink,
      lpBalanceReader,
      stakingRewardsReader,
      bridgeTokens,
      bridgeHistory,
      getBridgeQuote,
      getBridgeToTokens,
      getBridgeApproval,
      approveBridge,
      submitBridge,
      onReceive,
      onBridge,
      onSend,
    }),
    [
      resolvedEnv,
      theme,
      isMobile,
      open,
      resolvedDirection,
      resolvedModal,
      showBalance,
      showForgeToken,
      showGameToken,
      showQR,
      showBridge,
      qrLogoSrc,
      walletAddress,
      accountName,
      sendAccounts,
      profileImageUrl,
      connectorName,
      connectorIconUrl,
      preferredTokens,
      close,
      onSelectWallet,
      onCopyAddress,
      onBuy,
      onBuyDisabledMessage,
      onBridgeDisabledMessage,
      onSendDisabledMessage,
      onDisconnect,
      handleDisconnect,
      disconnectLabel,
      termsUrl,
      termsLabel,
      privacyUrl,
      privacyLabel,
      style,
      navContent,
      navPosition,
      footerContent,
      view,
      selectedSendToken,
      selectedDetailToken,
      showPortfolio,
      showOnePop,
      onePopSummary,
      onOnePopOpen,
      onOnePopSend,
      onOnePopClaim,
      onOnePopActivity,
      onOnePopRepliesSeen,
      onePopGetTokenSlot,
      onePopLogoSrc,
      onePopHeroSrc,
      portfolioTitle,
      portfolioSections,
      showTotalAssets,
      totalAssetsLabel,
      sendTransaction,
      getTransactionReceipt,
      estimateGas,
      onOutlink,
      lpBalanceReader,
      stakingRewardsReader,
      bridgeTokens,
      bridgeHistory,
      getBridgeQuote,
      getBridgeToTokens,
      getBridgeApproval,
      approveBridge,
      submitBridge,
      onReceive,
      onBridge,
      onSend,
    ],
  );

  return (
    <WalletInfoContext.Provider value={contextValue}>
      {isMobile ? (
        <Drawer.Root
          key="drawer"
          direction={resolvedDirection}
          open={open}
          onOpenChange={handleOpenChange}
          handleOnly={isSideDrawer}
          noBodyStyles
          repositionInputs={false}
        >
          {children}
        </Drawer.Root>
      ) : (
        <Popover.Root key="popover" open={open} onOpenChange={handleOpenChange}>
          {children}
        </Popover.Root>
      )}
    </WalletInfoContext.Provider>
  );
}

export const WalletInfo = Object.assign(WalletInfoRoot, {
  Trigger: WalletInfoTrigger,
  Content: WalletInfoContent,
  Nav: WalletInfoNav,
  Footer: WalletInfoFooter,
});
